import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useLogsApi custom hook for log operations.
 * - Provides methods to fetch logs using Apollo Client and GraphQL.
 * - Handles cache operations for logs.
 * - Exposes helpers for formatting timestamps and status colors.
 * - Returns methods for fetching logs and cache helpers.
 */

// GraphQL Queries
const LOGS_QUERIES = {
  GET_LOGS: gql`
    query GetLogs($filter: LogFilterInput) {
      GetLogs(filter: $filter) {
        logs {
          timestamp
          operation
          data
          email_id
          status
          error
          schema
          module
        }
        total
      }
    }
  `,
};

// Cache operations
const cacheOperations = {
  /**
   * Writes logs to Apollo Client cache.
   * @param {Array} logs - Array of log objects.
   * @param {ApolloClient} client - Apollo client instance.
   */
  writeLogs: (logs, client) => {
    if (!logs) return;
    client.writeQuery({
      query: LOGS_QUERIES.GET_LOGS,
      data: {
        GetLogs: {
          logs,
          total: logs.length,
        },
      },
    });
  },

  /**
   * Reads logs from Apollo Client cache.
   * @param {ApolloClient} client - Apollo client instance.
   * @returns {Array} Array of log objects or empty array if not found.
   */
  readLogs: (client) => {
    try {
      const { GetLogs } =
        client.readQuery({
          query: LOGS_QUERIES.GET_LOGS,
        }) || {};
      return GetLogs?.logs || [];
    } catch {
      return [];
    }
  },
};

export const useLogsApi = () => {
  /**
   * Formats a timestamp to a locale string.
   * @param {string|number} timestamp
   * @returns {string}
   */
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Returns a color string based on log status.
   * @param {string} status
   * @returns {string}
   */
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "success":
        return "success.main";
      case "error":
        return "error.main";
      default:
        return "text.primary";
    }
  };

  /**
   * Fetches logs from the backend with optional filters.
   * @param {Object} filter
   * @returns {Promise<{success: boolean, data?: Array, total?: number, error?: string}>}
   */
  const fetchLogs = async (filter = {}) => {
    try {
      const {
        page,
        limit,
        sortField,
        sortOrder,
        schema,
        operation,
        module,
        status,
        email_id,
        startDate,
        endDate,
      } = filter;

      // Clean filter object to remove undefined/null/empty values
      const cleanFilter = {
        page,
        limit,
        sortField,
        sortOrder,
        schema,
        operation,
        module,
        status,
        email_id,
        startDate,
        endDate,
      };

      Object.keys(cleanFilter).forEach((key) => {
        if (
          cleanFilter[key] === undefined ||
          cleanFilter[key] === null ||
          (Array.isArray(cleanFilter[key]) && cleanFilter[key].length === 0)
        ) {
          delete cleanFilter[key];
        }
      });

      const { data } = await client.query({
        query: LOGS_QUERIES.GET_LOGS,
        variables: {
          filter: cleanFilter,
        },
        fetchPolicy: "network-only",
      });

      return {
        success: true,
        data: data.GetLogs.logs,
        total: data.GetLogs.total,
      };
    } catch (error) {
      console.error("Error fetching logs:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    fetchLogs,
    cacheOperations,
    formatTimestamp,
    getStatusColor,
    LOGS_QUERIES,
  };
};
